/**
 *
 *  User Profile Page
 *  Bookings Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone"; // TODO: use dayjs instead

import { useAppState } from "../../../state";
import { useRegion } from "../../../context/RegionProvider";

// Components
import { WorkoutIcon } from "../../PartnerProductBadge/PartnerProductBadge";
import SessionLink from "../../SessionLink/SessionLink";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import TimeZoneLabel from "../../TimeZoneLabel/TimeZoneLabel";

// Icons
import SessionTypeIcon from "../SessionTypeIcon";
import CalendarIcon from "../../../icons/CalendarTruBeIcon";
import CaretDownIcon from "../../../icons/CaretDownIcon";
import MapMarkerIcon from "../../../icons/MapMarkerIcon";
import SessionTypeIconGreyedOut from "../../../icons/SessionTypeIconGreyedOut";

// Utils
import { getPartnerPictureUrl } from "../../../utils";
import { uniqueId } from "../../../utils/_";
import { getTz } from "../../../utils/date";
import { PAGE_SIZE } from "../../../constants";

// Images
import defaultPartnerPhoto from "../../../images/generic-trainerphoto.svg";

const BookingTile = ({
  className,
  trainerFirstName,
  trainerFamilyName,
  isCancelled,
  isPastBooking,
  productLabel,
  trainerPictureUrl,
  bookingId,
  bookingStartTimePart,
  bookingStartTimeAmPmPart,
  bookingStartTimeWeekdayPart,
  bookingStartTimeSecondPart,
  bookingStartTimeShort,
  onClick,
}) => {
  const [_id] = useState(`booking_tile_icon_${uniqueId()}`);

  return (
    <div
      className={`${className} tbk-relative tbk-flex tbk-cursor-pointer tbk-flex-row-reverse tbk-items-stretch tbk-overflow-hidden tbk-rounded-2xl tbk-shadow-trube md:tbk-flex-row`}
      onClick={onClick}
    >
      <div className="tb-profile-picture md:tbk-grow-1 tbk-relative tbk-max-h-[160px] tbk-shrink-0 tbk-basis-28 md:tbk-mr-2 md:tbk-max-h-[96px] md:tbk-basis-24">
        <img
          className="tb-profile-picture-img tbk-absolute tbk-h-full tbk-rounded-2xl tbk-object-cover"
          itemProp="image"
          src={trainerPictureUrl || defaultPartnerPhoto}
          alt="Coach profile"
        />
      </div>
      {isCancelled ? (
        <div className="tbk-text-small tbk-absolute tbk-left-0 tbk-bottom-0 tbk-rounded-lg tbk-bg-blue-grey tbk-py-0.5 tbk-px-1 tbk-text-basic-white">
          Cancelled
        </div>
      ) : null}

      <div className="tbk-flex tbk-min-w-0 tbk-grow tbk-flex-col tbk-justify-start tbk-py-2 md:tbk-grid md:tbk-grid-cols-account-booking-tile">
        <div className="tbk-order-3 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light">
          <div className="tbk-text-small md:tbk-text-h3-subtitle tbk-inline-block tbk-text-blue-grey md:tbk-mb-1 md:tbk-block md:tbk-text-primary">
            {trainerFirstName}&nbsp;
          </div>
          <div className="tbk-text-small md:tbk-text-h3-subtitle tbk-inline-block tbk-text-blue-grey md:tbk-block md:tbk-text-primary">
            {trainerFamilyName}
          </div>
        </div>
        <div className="tbk-order-1 tbk-mb-0.5 tbk-flex tbk-grow-0 tbk-items-center md:tbk-order-none md:tbk-mb-0 md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2">
          <div className="tbk-flex tbk-min-w-0 tbk-items-start">
            <div className="tbk-mr-1 tbk-hidden md:tbk-block">
              <CalendarIcon
                size={32}
                className={
                  isPastBooking ? "tbk-text-blue-grey" : "tbk-text-green"
                }
              />
            </div>
            <div className="tbk-text-main tbk-min-w-0 tbk-text-primary">
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {bookingStartTimePart}
                <span className="tbk-text-small !tbk-leading-none">
                  {bookingStartTimeAmPmPart}
                </span>{" "}
                {bookingStartTimeWeekdayPart},
              </div>
              <div className="tbk-hidden tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-block">
                {" "}
                {bookingStartTimeSecondPart}
              </div>
              <div className="md:tbk-hidden">{bookingStartTimeShort}</div>
            </div>
          </div>
        </div>
        <div className="tbk-order-2 tbk-flex tbk-min-w-0 tbk-grow-0 md:tbk-order-none md:tbk-border-r md:tbk-border-solid md:tbk-border-r-blue-grey-light md:tbk-px-2 xl:tbk-px-5">
          <div className="tbk-max-w-full">
            <div className="tbk-flex tbk-items-center">
              <div className="tbk-hidden md:tbk-block">
                <WorkoutIcon label={productLabel} size={32} />
              </div>
              <div className="tbk-text-secondary md:tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary md:tbk-mb-0">
                {productLabel}
                <span className="md:tbk-hidden"> with</span>
              </div>
            </div>
            <div className="tbk-hidden tbk-items-center md:tbk-flex">
              <div className="tbk-mr-0.5 tbk-text-green">
                <MapMarkerIcon size={32} />
              </div>
              <div className="tbk-text-main tbk-text-primary">In-Person</div>
            </div>
          </div>
        </div>
        <div className="tbk-hidden tbk-grow tbk-items-center tbk-justify-center md:tbk-flex">
          <SessionLink to={`/account/bookings/${bookingId}`} color="">
            View details
          </SessionLink>
        </div>
      </div>
      <div className="tbk-shrink-0 tbk-self-stretch tbk-p-2 md:tbk-hidden">
        {isPastBooking || isCancelled ? (
          <SessionTypeIconGreyedOut className="tbk-mr-1 tbk-text-blue-grey" />
        ) : (
          <SessionTypeIcon
            id={_id}
            className="tbk-mr-1 tbk-drop-shadow-trube"
          />
        )}
      </div>
    </div>
  );
};

export const Bookings = () => {
  const history = useHistory();
  const { region } = useRegion();

  const {
    user,
    bookings,
    pastBookings,
    loadBookings,
    loadPastBookings,
    pastBookingsPage,
    hasMorePastBookings,
    isPastBookingsFetching,
    clearPastBookingsReceive,
  } = useAppState();

  // Do not pass loadBookings callback or loadPastBookings callbacks, otherwise it'll load indefinitely.
  // It is okay not to pass loadPastBookings, as we call it only on mount.
  useEffect(() => {
    if (user && user.id) {
      loadBookings(
        user.id,
        "PAID,ACTIVE",
        {
          page: 0,
          size: PAGE_SIZE,
          sort: "start,desc",
        },
        user.region
      );
      loadPastBookings(
        user.id,
        "COMPLETED,CANCELLED",
        {
          page: 0,
          size: PAGE_SIZE,
          sort: "start,desc",
        },
        user.region
      );
    }

    return () => {
      clearPastBookingsReceive();
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMorePastBookings = useCallback(
    (page = 0) => {
      const size = PAGE_SIZE;

      if (isPastBookingsFetching) {
        return;
      }

      loadPastBookings(
        user.id,
        "COMPLETED,CANCELLED",
        {
          page,
          size,
          sort: "start,desc",
        },
        user.region
      );
    },
    [user, loadPastBookings, isPastBookingsFetching]
  );

  return (
    <>
      <ScrollToTopOnMount />
      <div>
        <div className="tbk-mb-5">
          <div className="tbk-text-title-bold tbk-mt-4 tbk-mb-3 tbk-text-primary md:tbk-mt-4 md:tbk-mb-4">
            Upcoming Session
          </div>
          <div className="tbk-text-small tbk-mb-3 tbk-hidden tbk-text-blue-grey md:tbk-mb-4">
            Time set to{" "}
            <TimeZoneLabel
              region={user ? user.region : process.env.REACT_APP_REGION}
            />
          </div>
          {bookings && bookings.length ? (
            bookings.map((booking) => (
              <BookingTile
                key={booking.id}
                className="tbk-mb-2"
                trainerPictureUrl={getPartnerPictureUrl(booking.partner)}
                trainerFirstName={booking.partner.firstName}
                trainerFamilyName={booking.partner.lastName}
                productLabel={booking.product.name}
                bookingId={booking.id}
                bookingStartTimePart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("h:mm")}
                bookingStartTimeAmPmPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("A")}
                bookingStartTimeWeekdayPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("dddd")}
                bookingStartTimeSecondPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("MMMM D, YYYY")}
                bookingStartTimeShort={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("h:mma dddd, MMM D")}
                onClick={() => history.push(`/account/bookings/${booking.id}`)}
              />
            ))
          ) : (
            <div className="tbk-text-main tbk-mt-1 tbk-text-center tbk-text-primary">
              You have no upcoming sessions
            </div>
          )}
        </div>
        <hr />
        <div>
          <div className="tbk-text-title-bold tbk-mt-5 tbk-mb-3 tbk-text-primary">
            Past Sessions
          </div>
          {pastBookings && pastBookings.length ? (
            pastBookings.map((booking) => (
              <BookingTile
                key={booking.id}
                className="tbk-mb-2"
                trainerPictureUrl={getPartnerPictureUrl(booking.partner)}
                trainerFirstName={booking.partner.firstName}
                trainerFamilyName={booking.partner.lastName}
                productLabel={booking.product.name}
                isCancelled={booking.status === "CANCELLED"}
                isPastBooking={true}
                bookingId={booking.id}
                bookingStartTimePart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("h:mm")}
                bookingStartTimeAmPmPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("A")}
                bookingStartTimeWeekdayPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("dddd")}
                bookingStartTimeSecondPart={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("MMMM D, YYYY")}
                bookingStartTimeShort={moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format("h:mma dddd, MMM D")}
                onClick={() => history.push(`/account/bookings/${booking.id}`)}
              />
            ))
          ) : (
            <div className="tbk-text-main tbk-mt-1 tbk-text-center tbk-text-primary">
              You have no past sessions
            </div>
          )}
        </div>

        {pastBookings &&
        pastBookings.length >= PAGE_SIZE &&
        hasMorePastBookings &&
        !isPastBookingsFetching ? (
          <div className="tbk-relative tbk-mt-5 tbk-mb-7 tbk-text-center">
            <button
              className="tbk-inline-block tbk-bg-grey-light tbk-py-0 tbk-px-[19px]"
              onClick={() => loadMorePastBookings(pastBookingsPage + 1)}
            >
              <div className="tbk-flex tbk-text-blue">
                Show More
                <CaretDownIcon />
              </div>
            </button>
            <hr className="tbk-mx-auto tbk-mb-0 tbk--mt-1.5 tbk-border-blue-grey-light" />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Bookings;
